import { useCallback, useEffect, useMemo, useState } from 'react';
import { LanguageContext } from './context';
import { translations } from './translations';
import { getCurrentUserPreferences, updateCurrentUserLang } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const STORAGE_KEY = 'lang';

// 从环境变量获取默认语言（仅支持 zh/en），默认为 en
const DEFAULT_LANG = (() => {
  const raw = import.meta.env.VITE_DEFAULT_LANG;
  const lowered = (raw || 'en').toLowerCase();
  if (lowered.startsWith('zh')) return 'zh';
  return 'en';
})();

function normalizeLang(lang) {
  const normalized = (lang || '').toLowerCase();
  if (normalized.startsWith('zh')) return 'zh';
  if (normalized.startsWith('en')) return 'en';
  return DEFAULT_LANG;
}

function getInitialLang() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) return normalizeLang(stored);
  return DEFAULT_LANG;
}

function persistLang(lang) {
  localStorage.setItem(STORAGE_KEY, lang);
}

function resolveKey(obj, key) {
  if (!key) return undefined;
  return key.split('.').reduce((acc, part) => (acc ? acc[part] : undefined), obj);
}

function interpolate(template, params) {
  if (!template || !params) return template;
  return template.replace(/\{(\w+)\}/g, (_, name) => {
    const value = params[name];
    return value === undefined || value === null ? '' : String(value);
  });
}

export function LanguageProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [lang, setLang] = useState(getInitialLang);

  useEffect(() => {
    let cancelled = false;
    async function sync() {
      if (!isAuthenticated) return;
      try {
        const res = await getCurrentUserPreferences();
        const serverLang = normalizeLang(res?.data?.lang);
        if (!cancelled && serverLang && serverLang !== lang) {
          persistLang(serverLang);
          setLang(serverLang);
        }
      } catch {
        // ignore
      }
    }
    sync();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const t = useCallback(
    (key, params) => {
      const normalized = normalizeLang(lang);
      const bundle = translations[normalized] || translations[DEFAULT_LANG] || translations.en;
      const fallback = translations[DEFAULT_LANG] || translations.en;
      const template = resolveKey(bundle, key) ?? resolveKey(fallback, key) ?? key;
      return interpolate(template, params);
    },
    [lang],
  );

  const changeLang = useCallback(
    async (nextLang) => {
      const normalized = normalizeLang(nextLang);
      persistLang(normalized);
      setLang(normalized);
      if (!isAuthenticated) return;
      try {
        await updateCurrentUserLang(normalized);
      } catch {
        // ignore
      }
    },
    [isAuthenticated],
  );

  const value = useMemo(
    () => ({
      lang,
      setLang: changeLang,
      t,
      toggleLang: () => changeLang(lang === 'zh' ? 'en' : 'zh'),
    }),
    [lang, changeLang, t],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
